import React from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { executeMutation, executeQuery } from 'firebase/data-connect';
import {
  createChatRef,
  deleteChatPinRef,
  deleteChatMessageRef,
  GetChatPinsByChatData,
  getChatPinsByChatRef,
  getChatRef,
  getChatsByThreadRef,
  getMessageReadsByChatRef,
  updateChatReadRef,
  upsertChatPinRef,
  upsertChatMessageReadRef,
} from '../dataconnect-generated';
import { getChatDataConnect } from '../lib/dataConnect';
import { decryptChatMessages, encryptChatMessage } from '../lib/chatCrypto';
import { deleteChatFile, downloadChatFile, uploadChatFile } from '../lib/chatFile';
import { useAuth } from '../contexts/AuthContext';
import { navSlice } from '../redux/config/leftnav';

export type ChatMessage = {
  messageUuid: string;
  senderUid: string;
  senderUserName: string;
  isAdmin: boolean;
  body: string;
  fileName?: string | null;
  realFileName?: string | null;
  createdAt: string;
  readCount: number;
  isPinned: boolean;
};

export type PinnedChatMessage = {
  messageUuid: string;
  preview: string;
  realFileName?: string | null;
};

export type TogglePinResult = 'pinned' | 'unpinned' | 'limitReached';

const POLLING_INTERVAL_MS = 60_000;
const LOAD_LIMIT = 20;
const MAX_PINNED_MESSAGES = 3;
const FUTURE_DATE = new Date('9999-12-31T23:59:59.999Z').toISOString();
type FetchMode = 'initial' | 'refresh' | 'loadMore';
type ChatPin = GetChatPinsByChatData['chatPins'][number];

/**
 * Angular サンプル相当のフローで、チャット表示/送信/既読更新を行う。
 */
export const useChatMessages = (nursingHomeId: number, senderUserName: string) => {
  const { currentUser } = useAuth();
  const dispatch = useDispatch();
  const authUid = currentUser.user?.uid;
  const [messages, setMessages] = React.useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [isSending, setIsSending] = React.useState(false);
  const [isDeletingMessage, setIsDeletingMessage] = React.useState(false);
  const [isUpdatingPin, setIsUpdatingPin] = React.useState(false);
  const [pinnedMessages, setPinnedMessages] = React.useState<PinnedChatMessage[]>([]);
  const [error, setError] = React.useState<Error | null>(null);
  const [hasMore, setHasMore] = React.useState(false);
  const messagesRef = React.useRef<ChatMessage[]>([]);
  const oldestMessageCreatedAt = messages[0]?.createdAt;

  React.useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const loadPinnedMessages = React.useCallback(async (pins?: ChatPin[]) => {
    if (!nursingHomeId || !authUid) {
      setPinnedMessages([]);
      return;
    }

    const chatPins = pins ?? (
      await executeQuery(getChatPinsByChatRef(getChatDataConnect(), { nursingHomeId }))
    ).data.chatPins;
    const sortedChatPins = [...chatPins].sort((a, b) => a.pinnedAt.localeCompare(b.pinnedAt));
    const pinnedChats = await Promise.all(
      sortedChatPins.map(async (pin) => {
        const result = await executeQuery(
          getChatRef(getChatDataConnect(), {
            nursingHomeId,
            messageUuid: pin.messageUuid,
          }),
        );
        return result.data.chats[0];
      }),
    );
    const chats = pinnedChats.filter((chat): chat is NonNullable<typeof chat> => Boolean(chat));
    const encryptedMessages = chats
      .filter((chat) => chat.text && chat.iv)
      .map((chat) => ({
        messageUuid: chat.messageUuid,
        text: chat.text as string,
        iv: chat.iv as string,
      }));
    const decryptedMessages = encryptedMessages.length
      ? await decryptChatMessages(nursingHomeId, encryptedMessages)
      : [];
    const decryptedMessageMap = new Map(
      decryptedMessages.map((message) => [message.messageUuid, message.text]),
    );

    setPinnedMessages(
      chats.map((chat) => {
        const body = decryptedMessageMap.get(chat.messageUuid) ?? chat.text ?? '';
        const preview = body.trim()
          ? body.length > 40
            ? `${body.substring(0, 40)}...`
            : body
          : chat.realFileName
          ? chat.realFileName
          : '';
        return { messageUuid: chat.messageUuid, preview, realFileName: chat.realFileName };
      }),
    );
  }, [authUid, nursingHomeId]);

  const fetchMessages = React.useCallback(
    async (mode: FetchMode, oldestMessageCreatedAt = FUTURE_DATE): Promise<ChatPin[]> => {
      if (!nursingHomeId || !authUid) {
        setIsLoading(false);
        return [];
      }

      const nowIso = new Date().toISOString();
      const isLoadingPastMessages = mode === 'loadMore';
      const targetCreatedAt = isLoadingPastMessages ? oldestMessageCreatedAt : FUTURE_DATE;
      let chatPins: ChatPin[] = [];

      try {
        if (mode === 'initial') {
          setIsLoading(true);
        } else if (isLoadingPastMessages) {
          setIsLoadingMore(true);
        }

        const readResult = await executeQuery(
          getMessageReadsByChatRef(getChatDataConnect(), {
            nursingHomeId,
          }),
        );
        const pinsResult = await executeQuery(
          getChatPinsByChatRef(getChatDataConnect(), { nursingHomeId }),
        );
        chatPins = pinsResult.data.chatPins;
        const pinSet = new Set(chatPins.map((pin) => pin.messageUuid));

        const readSet = new Set<string>();
        const readCountMap = new Map<string, number>();

        for (const read of readResult.data.chatMessageReads) {
          const key = `${read.messageId}_${read.userUuid}`;
          readSet.add(key);

          if (read.userUuid === authUid) {
            continue;
          }

          readCountMap.set(read.messageId, (readCountMap.get(read.messageId) ?? 0) + 1);
        }

        const result = await executeQuery(
          getChatsByThreadRef(getChatDataConnect(), {
            nursingHomeId,
            createdAt: targetCreatedAt,
          }),
        );
        let chats = result.data.chats;
        if (mode === 'refresh') {
          const oldestLoadedCreatedAt = messagesRef.current[0]?.createdAt;
          let page = chats;

          while (
            oldestLoadedCreatedAt &&
            page.length >= LOAD_LIMIT &&
            page[page.length - 1].createdAt > oldestLoadedCreatedAt
          ) {
            const nextResult = await executeQuery(
              getChatsByThreadRef(getChatDataConnect(), {
                nursingHomeId,
                createdAt: page[page.length - 1].createdAt,
              }),
            );
            page = nextResult.data.chats;
            chats = [...chats, ...page];
          }

          if (oldestLoadedCreatedAt) {
            chats = chats.filter((chat) => chat.createdAt >= oldestLoadedCreatedAt);
          }
        }
        if (mode !== 'refresh') {
          setHasMore(chats.length >= LOAD_LIMIT);
        }

        await executeMutation(
          updateChatReadRef(getChatDataConnect(), {
            nursingHomeId,
            userUuid: authUid,
            lastReadAt: nowIso,
          }),
        );
        dispatch(navSlice.actions.setChatUnreadCount(0));

        await Promise.all(
          chats
            .filter((item) => !readSet.has(`${item.messageUuid}_${authUid}`))
            .map((item) =>
              executeMutation(
                upsertChatMessageReadRef(getChatDataConnect(), {
                  nursingHomeId,
                  messageId: item.messageUuid,
                  userUuid: authUid,
                  readAt: nowIso,
                }),
              ),
            ),
        );

        const encryptedMessages = chats
          .filter((item) => item.text && item.iv)
          .map((item) => ({
            messageUuid: item.messageUuid,
            text: item.text as string,
            iv: item.iv as string,
          }));
        const decryptedMessages = encryptedMessages.length
          ? await decryptChatMessages(nursingHomeId, encryptedMessages)
          : [];
        const decryptedMessageMap = new Map(
          decryptedMessages.map((message) => [message.messageUuid, message.text]),
        );

        const transformed: ChatMessage[] = chats.map((item) => ({
          messageUuid: item.messageUuid,
          senderUid: item.senderUuid,
          senderUserName: item.senderUserName,
          isAdmin: item.senderUuid !== authUid,
          body: decryptedMessageMap.get(item.messageUuid) ?? item.text ?? '',
          fileName: item.fileName,
          realFileName: item.realFileName,
          createdAt: item.createdAt,
          readCount: readCountMap.get(item.messageUuid) ?? 0,
          isPinned: pinSet.has(item.messageUuid),
        }));

        const sortedMessages = transformed.reverse();
        setMessages((prev) => {
          if (mode === 'initial' || mode === 'refresh') {
            return sortedMessages;
          }

          const messageMap = new Map(
            prev.map((message) => [
              message.messageUuid,
              { ...message, isPinned: pinSet.has(message.messageUuid) },
            ]),
          );
          for (const message of sortedMessages) {
            messageMap.set(message.messageUuid, message);
          }
          return Array.from(messageMap.values()).sort((a, b) =>
            a.createdAt.localeCompare(b.createdAt),
          );
        });
        setError(null);
      } catch (err) {
        setError(err as Error);
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }

      return chatPins;
    },
    [authUid, dispatch, nursingHomeId],
  );

  const loadMore = React.useCallback(async () => {
    if (isLoadingMore || !hasMore || !oldestMessageCreatedAt) {
      return;
    }
    await fetchMessages('loadMore', oldestMessageCreatedAt);
  }, [fetchMessages, hasMore, isLoadingMore, oldestMessageCreatedAt]);

  const sendMessage = React.useCallback(
    async (body: string, attachment?: File | null) => {
      const trimmed = body.trim();
      if ((!trimmed && !attachment) || !nursingHomeId || !authUid) return;
      setIsSending(true);
      try {
        const encrypted = trimmed ? await encryptChatMessage(nursingHomeId, trimmed) : null;
        const uploadedFile = attachment ? await uploadChatFile(nursingHomeId, attachment) : null;
        await executeMutation(
          createChatRef(getChatDataConnect(), {
            nursingHomeId,
            messageUuid: uuid(),
            senderUuid: authUid,
            senderUserName,
            text: encrypted?.text ?? null,
            iv: encrypted?.iv ?? null,
            realFileName: uploadedFile?.realFileName ?? null,
            fileName: uploadedFile?.fileName ?? null,
          }),
        );
        await fetchMessages('refresh');
      } finally {
        setIsSending(false);
      }
    },
    [authUid, fetchMessages, nursingHomeId, senderUserName],
  );

  const downloadAttachment = React.useCallback(
    async (fileName: string) => downloadChatFile(nursingHomeId, fileName),
    [nursingHomeId],
  );

  const togglePin = React.useCallback(
    async (message: ChatMessage): Promise<TogglePinResult> => {
      if (!message.isPinned && pinnedMessages.length >= MAX_PINNED_MESSAGES) {
        return 'limitReached';
      }

      setIsUpdatingPin(true);
      try {
        if (message.isPinned) {
          await executeMutation(
            deleteChatPinRef(getChatDataConnect(), {
              nursingHomeId,
              messageUuid: message.messageUuid,
            }),
          );
        } else {
          await executeMutation(
            upsertChatPinRef(getChatDataConnect(), {
              nursingHomeId,
              messageUuid: message.messageUuid,
              pinnedAt: new Date().toISOString(),
            }),
          );
        }

        setMessages((current) =>
          current.map((item) =>
            item.messageUuid === message.messageUuid
              ? { ...item, isPinned: !message.isPinned }
              : item,
          ),
        );
        await loadPinnedMessages();
        return message.isPinned ? 'unpinned' : 'pinned';
      } finally {
        setIsUpdatingPin(false);
      }
    },
    [loadPinnedMessages, nursingHomeId, pinnedMessages.length],
  );

  const deleteMessage = React.useCallback(
    async (message: ChatMessage) => {
      if (!authUid || message.senderUid !== authUid) {
        throw new Error('Only the sender can delete this message');
      }
      if (message.isPinned) {
        throw new Error('Pinned messages cannot be deleted');
      }

      setIsDeletingMessage(true);
      try {
        await executeMutation(
          deleteChatMessageRef(getChatDataConnect(), {
            messageUuid: message.messageUuid,
            senderUuid: authUid,
            deletedAt: new Date().toISOString(),
            deletedBy: authUid,
          }),
        );

        if (message.fileName) {
          try {
            await deleteChatFile(nursingHomeId, message.fileName);
          } catch (err) {
            // メッセージの削除は完了しているため、添付削除の失敗だけを記録する。
            console.warn('Failed to delete chat attachment', err);
          }
        }

        setMessages((current) =>
          current.filter((item) => item.messageUuid !== message.messageUuid),
        );
      } finally {
        setIsDeletingMessage(false);
      }
    },
    [authUid, nursingHomeId],
  );

  React.useEffect(() => {
    setMessages([]);
    setPinnedMessages([]);
    setError(null);
    setHasMore(false);
    fetchMessages('initial')
      .then((pins) => loadPinnedMessages(pins))
      .catch((err) => setError(err as Error));
    const timer = setInterval(() => fetchMessages('refresh'), POLLING_INTERVAL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [fetchMessages, loadPinnedMessages]);

  return {
    messages,
    isLoading,
    isLoadingMore,
    isSending,
    isDeletingMessage,
    isUpdatingPin,
    pinnedMessages,
    error,
    hasMore,
    fetchMessages,
    loadMore,
    sendMessage,
    downloadAttachment,
    togglePin,
    deleteMessage,
  };
};
